/**
 * CreditReports — /admin/credit-reports
 *
 * FREEZE-ADJACENT. This surface renders Credit Engine report data via
 * admin APIs. Wave 6 Batch 3 · Cluster A Turn 2 chrome sweep — visual
 * chrome ONLY. The data pipeline (fetch / retry / drawer detail load)
 * and every business field rendered from `detail.*` is preserved
 * BYTE-EXACT.
 *
 * Anchor: CreditEngineEvents (Wave 5-Late).
 *   • `_primitives.Section` shells for filter + list + drawer sections.
 *   • `_primitives.SelectInput` / `TextInput` for filters.
 *   • `_primitives.GhostButton` for Retry / drawer actions.
 *   • STATUS_COLOR pastel map REMOVED. `StatusPill` now emits a DS
 *     `Badge` with `shape="sharp"` + STATUS_TONE map:
 *       completed  → successMuted
 *       processing → warning
 *       pending    → accent
 *       failed     → danger
 *   • Dense hairline table `px-4 py-1.5` + `isLast` guard, tabular-nums
 *     font-mono on numeric cells.
 *   • Drawer chrome swept to `--ds-*`; backdrop `rgba(27,26,22,0.55)`.
 *
 * Retry mechanism, filter payloads, and testids preserved BYTE-EXACT.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, RefreshCw, Search, X } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { Badge } from "@/design-system/components/Badge";
import { Heading } from "@/design-system/components/Heading";
import { api, formatApiError } from "@/api/client";
import { Section, SelectInput, TextInput, GhostButton } from "@/admin/_primitives";

const STATUSES = ["pending", "processing", "completed", "failed"];

const STATUS_TONE = {
  completed:  "successMuted",
  processing: "warning",
  pending:    "accent",
  failed:     "danger",
};

function StatusPill({ v }) {
  if (!v) return <span style={{ color: "var(--ds-color-ink-subtle)" }}>—</span>;
  return (
    <Badge
      tone={STATUS_TONE[v] || "neutralMuted"}
      size="sm"
      shape="sharp"
      className="uppercase tracking-[0.1em]"
    >
      {v}
    </Badge>
  );
}

export default function AdminCreditReports() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [provider, setProvider] = useState("");
  const [negFilter, setNegFilter] = useState("");
  const [openId, setOpenId] = useState(null);
  const [retryBusy, setRetryBusy] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/credit-reports");
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const providers = useMemo(() => {
    const s = new Set();
    items.forEach((i) => i.provider_used && s.add(i.provider_used));
    return Array.from(s).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const term = debouncedQ.trim().toLowerCase();
    return items.filter((row) => {
      if (term && !(`${row.user_email || ""} ${row.original_filename || ""}`).toLowerCase().includes(term)) return false;
      if (statusFilter && row.extraction_status !== statusFilter) return false;
      if (provider && row.provider_used !== provider) return false;
      if (negFilter === "with" && (row.negative_count || 0) === 0) return false;
      if (negFilter === "without" && (row.negative_count || 0) > 0) return false;
      return true;
    });
  }, [items, debouncedQ, statusFilter, provider, negFilter]);

  async function handleRetry(row) {
    setRetryBusy(row.id);
    try {
      await api.post(`/admin/credit-reports/${row.id}/retry`);
      toast.success("Retry queued — live Gemini re-extraction running");
      setTimeout(reload, 1200);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRetryBusy(null); }
  }

  return (
    <AdminLayout title="Credit reports" subtitle={`${items.length} reports uploaded`}>
      <div className="mb-3 max-w-3xl">
        <AdminPageHelp
          title="Credit Reports"
          purpose="Review uploaded credit reports, OCR extraction quality, and per-report analysis results."
          configures="View extraction outputs, re-run analysis, download the source PDF."
          impact="Re-running analysis re-computes the readiness score and roadmap for that member."
          safety="Original uploads are retained for the retention window; extraction outputs can be re-generated at any time."
        />
      </div>

      <Section title="Filter" testid="credit-reports-filter-section">
        <div className="grid md:grid-cols-[1fr_auto_auto_auto] gap-2 items-end">
          <label className="block relative">
            <Search
              className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              style={{ color: "var(--ds-color-ink-subtle)" }}
              aria-hidden="true"
            />
            <TextInput
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by user email or filename…"
              data-testid="credit-reports-search"
              className="w-full pl-9"
            />
          </label>
          <SelectInput
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="credit-reports-filter-status"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </SelectInput>
          <SelectInput
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            data-testid="credit-reports-filter-provider"
          >
            <option value="">All providers</option>
            {providers.map((p) => <option key={p} value={p}>{p}</option>)}
          </SelectInput>
          <SelectInput
            value={negFilter}
            onChange={(e) => setNegFilter(e.target.value)}
            data-testid="credit-reports-filter-neg"
          >
            <option value="">Negatives: any</option>
            <option value="with">With negatives</option>
            <option value="without">No negatives</option>
          </SelectInput>
        </div>
      </Section>

      {loading ? (
        <div className="text-[13px] py-6 text-center" style={{ color: "var(--ds-color-ink-muted)" }}>
          Loading…
        </div>
      ) : (
        <Section
          title="Reports"
          testid="credit-reports-section"
          bodyClassName="p-0"
          badge={
            <span
              className="text-[11px] uppercase tracking-[0.14em] tabular-nums"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              {filtered.length} showing · {items.length} total
            </span>
          }
        >
          <div className="overflow-x-auto" data-testid="credit-reports-table">
            <table className="w-full text-[13px]">
              <thead>
                <tr
                  className="text-left text-[10px] uppercase tracking-[0.16em] font-semibold"
                  style={{
                    color: "var(--ds-color-ink-subtle)",
                    background: "var(--ds-color-admin-surface-muted)",
                    borderBottom: "1px solid var(--ds-color-admin-border)",
                  }}
                >
                  <th className="px-4 py-2">User</th>
                  <th className="px-4 py-2">Filename</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Provider</th>
                  <th className="px-4 py-2 text-right">Score</th>
                  <th className="px-4 py-2 text-right">Negs</th>
                  <th className="px-4 py-2">Created</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, idx) => {
                  const isLast = idx === filtered.length - 1;
                  return (
                    <tr
                      key={row.id}
                      data-testid={`credit-reports-row-${row.id}`}
                      className="cursor-pointer transition-colors"
                      onClick={() => setOpenId(row.id)}
                      style={{
                        borderBottom: isLast ? "none" : "1px solid var(--ds-color-admin-border)",
                        transitionDuration: "var(--ds-duration-fast)",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "var(--ds-color-admin-surface-muted)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      <td
                        className="px-4 py-1.5 text-[12px] truncate max-w-[200px]"
                        style={{ color: "var(--ds-color-ink)" }}
                      >
                        {row.user_email || "—"}
                      </td>
                      <td
                        className="px-4 py-1.5 text-[12px] truncate max-w-[220px]"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.original_filename}
                      </td>
                      <td className="px-4 py-1.5"><StatusPill v={row.extraction_status} /></td>
                      <td
                        className="px-4 py-1.5 text-[12px] font-mono"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.provider_used || "—"}
                      </td>
                      <td
                        className="px-4 py-1.5 text-[12px] text-right tabular-nums font-mono"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.score_extracted ?? "—"}
                      </td>
                      <td
                        className="px-4 py-1.5 text-[12px] text-right tabular-nums font-mono"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.negative_count ?? 0}
                      </td>
                      <td
                        className="px-4 py-1.5 text-[12px] tabular-nums font-mono"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.created_at?.slice ? row.created_at.slice(0, 10) : String(row.created_at || "").slice(0, 10)}
                      </td>
                      <td className="px-4 py-1.5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {row.extraction_status === "failed" && (
                            <button
                              data-testid={`credit-reports-retry-${row.id}`}
                              disabled={retryBusy === row.id}
                              onClick={(e) => { e.stopPropagation(); handleRetry(row); }}
                              className="text-[11px] uppercase tracking-[0.12em] font-semibold hover:underline inline-flex items-center gap-1 disabled:opacity-60"
                              style={{ color: "var(--ds-color-accent-strong)" }}
                            >
                              <RefreshCw className={`w-3 h-3 ${retryBusy === row.id ? "animate-spin" : ""}`} /> Retry
                            </button>
                          )}
                          <button
                            data-testid={`credit-reports-open-${row.id}`}
                            onClick={(e) => { e.stopPropagation(); setOpenId(row.id); }}
                            className="text-[11px] uppercase tracking-[0.12em] font-semibold hover:underline"
                            style={{ color: "var(--ds-color-accent-strong)" }}
                          >
                            Open
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-4 py-8 text-center text-[13px]"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      No reports match.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {openId && <ReportDrawer reportId={openId} onClose={() => setOpenId(null)} onRetry={handleRetry} />}
    </AdminLayout>
  );
}

function ReportDrawer({ reportId, onClose, onRetry }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/admin/credit-reports/${reportId}`).then((r) => { if (!cancel) setDetail(r.data); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [reportId]);

  return (
    <div className="fixed inset-0 z-50" data-testid="credit-reports-drawer-overlay">
      <div
        className="absolute inset-0"
        style={{ background: "rgba(27, 26, 22, 0.55)" }}
        onClick={onClose}
      />
      <div
        data-testid="credit-reports-drawer"
        className="absolute right-0 top-0 bottom-0 w-full max-w-2xl overflow-y-auto"
        style={{
          background: "var(--ds-color-surface)",
          borderLeft: "1px solid var(--ds-color-admin-border)",
          boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
        }}
      >
        <header
          className="sticky top-0 flex items-center justify-between px-5 py-3.5 z-10 border-b"
          style={{
            background: "var(--ds-color-surface)",
            borderColor: "var(--ds-color-admin-border)",
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={onClose}
              data-testid="credit-reports-drawer-back"
              className="inline-flex items-center gap-1 text-[12px] uppercase tracking-[0.12em] transition-colors"
              style={{ color: "var(--ds-color-ink-muted)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to list
            </button>
            <div className="min-w-0">
              <div
                className="text-[10px] uppercase tracking-[0.18em] font-semibold"
                style={{ color: "var(--ds-color-accent-strong)" }}
              >
                Credit report
              </div>
              <Heading level={4} size="md" weight="semibold" className="truncate">
                {detail?.original_filename || "…"}
              </Heading>
            </div>
          </div>
          <button
            data-testid="credit-reports-drawer-close"
            onClick={onClose}
            className="transition-colors"
            style={{ color: "var(--ds-color-ink-muted)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
          >
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="p-5 space-y-4">
          {loading && (
            <div className="text-sm" style={{ color: "var(--ds-color-ink-muted)" }}>Loading…</div>
          )}
          {detail && (
            <>
              <Section title="Meta" testid="credit-reports-drawer-meta">
                <Grid>
                  <Meta label="User" value={detail.user_email} />
                  <Meta label="Status" value={detail.extraction_status} />
                  <Meta label="Provider" value={detail.provider_used || "—"} />
                  <Meta label="Extraction ms" value={detail.extraction_ms != null ? `${detail.extraction_ms}ms` : "—"} />
                  <Meta label="Bureau" value={detail.bureau || "—"} />
                  <Meta label="Score" value={detail.score_extracted ?? "—"} />
                </Grid>
              </Section>
              {detail.extraction_status === "failed" && (
                <Section title="Error" testid="credit-reports-drawer-error-section">
                  <p className="text-sm" style={{ color: "var(--ds-color-danger)" }}>
                    {detail.extraction_error || "Unknown error"}
                  </p>
                  <div className="mt-2">
                    <GhostButton
                      data-testid="credit-reports-drawer-retry"
                      onClick={() => onRetry({ id: detail.id })}
                    >
                      <RefreshCw className="w-3 h-3" /> Retry extraction
                    </GhostButton>
                  </div>
                </Section>
              )}
              <Section
                title={`Negative items (${(detail.negative_items || []).length})`}
                testid="credit-reports-drawer-negatives-section"
              >
                {(detail.negative_items || []).length === 0 ? (
                  <p className="text-sm" style={{ color: "var(--ds-color-ink-muted)" }}>None</p>
                ) : (
                  <ul className="text-sm space-y-2">
                    {(detail.negative_items || []).slice(0, 25).map((n, i, arr) => {
                      const isLast = i === Math.min(24, arr.length - 1);
                      return (
                        <li
                          key={i}
                          className="pb-2"
                          style={{
                            borderBottom: isLast ? "none" : "1px solid var(--ds-color-admin-border)",
                          }}
                        >
                          <div style={{ color: "var(--ds-color-ink)" }}>
                            {n.creditor_name || n.account_name || `Item ${i + 1}`}
                          </div>
                          <div className="text-xs" style={{ color: "var(--ds-color-ink-muted)" }}>
                            {n.negative_type} · {n.bureau || "—"}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Section>
              <Section
                title={`Accounts (${(detail.accounts || []).length})`}
                testid="credit-reports-drawer-accounts-section"
              >
                {(detail.accounts || []).length === 0 ? (
                  <p className="text-sm" style={{ color: "var(--ds-color-ink-muted)" }}>None</p>
                ) : (
                  <ul className="text-sm space-y-1">
                    {(detail.accounts || []).slice(0, 20).map((a, i) => (
                      <li key={i} className="flex items-center justify-between text-xs">
                        <span
                          className="truncate max-w-[260px]"
                          style={{ color: "var(--ds-color-ink-muted)" }}
                        >
                          {a.creditor_name || a.account_name}
                        </span>
                        <span
                          className="tabular-nums font-mono"
                          style={{ color: "var(--ds-color-ink-muted)" }}
                        >
                          {a.balance != null ? `$${a.balance}` : "—"}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 gap-x-4 text-xs">{children}</div>;
}

function Meta({ label, value }) {
  return (
    <div>
      <div
        className="text-[10px] uppercase tracking-[0.12em] font-semibold"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {label}
      </div>
      <div className="text-sm" style={{ color: "var(--ds-color-ink)" }}>
        {value || "—"}
      </div>
    </div>
  );
}
